import {
  applyStateDelta,
  rollDice,
  type GameModule,
  type GameTurnInput,
  type GameTurnOutcome,
  type ViewSpec,
  type ViewWidget,
} from "@roqeet/core";

/**
 * 텍스트 어드벤처 — 네이티브 게임 모듈(결정 #24/#25). 추상화(GameModule +
 * 턴 프로토콜#26 + viewSpec#27)를 실증하는 첫 구조화 게임. 채팅과 가장 가까워
 * 리스크가 낮으면서도 선택지·인벤토리·상태 변화·엔딩을 모두 다룬다.
 *
 * 상태 모양(moduleState):
 *   { hp, maxHp, location, inventory: string[], flags..., ended, ending }
 */

const DEFAULT_STATE = (t?: (k: string, ...args: any[]) => string): Record<string, unknown> => ({
  hp: 100,
  maxHp: 100,
  location: t ? t("game.state.startingPoint") : "시작 지점",
  inventory: [],
});

/** 모델에 RoQeet 게임 명령 문법을 지시(결정 #26). 현재 상태를 함께 알려 일관성을 높인다. */
function protocolInstructions(state: Record<string, unknown>, t?: (k: string, ...args: any[]) => string): string {
  const hp = num(state["hp"], 100);
  const maxHp = num(state["maxHp"], 100);
  const location = String(state["location"] ?? "알 수 없음");
  const inventory = Array.isArray(state["inventory"]) ? (state["inventory"] as unknown[]) : [];
  const ended = state["ended"] === true;
  
  const tf = (k: string, fallback: string, ...args: any[]) => t ? t(k, ...args) : fallback;

  return [
    tf("game.prompt.ta.p1", "당신은 텍스트 어드벤처 게임의 게임마스터입니다. 2인칭 시점으로 장면을 생생하게 묘사하세요."),
    "응답은 본문 서술 뒤에 RoQeet 게임 명령으로 상태를 갱신하세요(필요할 때만).",
    "- 선택지: @선택지 { ... } 블록에 보통 2~4개를 한 줄씩 작성",
    '- 상태 변경: @설정 location = "동굴" / @감소 hp 10 / @증가 hp 5',
    '- 아이템 획득: @추가 inventory "녹슨 검" (소모는 본문으로 묘사)',
    "- 게임 종료(사망/엔딩): @설정 ended = true 와 함께 결말을 서술",
    "본문은 자유롭게 쓰되 RoQeet 명령은 정확한 문법으로 응답 끝에 모으세요. 플레이어가 죽거나 목표를 이루면 게임을 끝내세요.",
    "",
    tf("game.prompt.ta.p8", "현재 상태 — HP: {hp}/{maxHp}, 위치: {location}, 소지품: [{inventory}]{ended}", 
      { hp, maxHp, location, inventory: inventory.join(", ") || tf("game.prompt.ta.p10", "없음"), ended: ended ? tf("game.prompt.ta.p9", " (게임 종료됨)") : "" })
  ].join("\n");
}

/** 파싱된 턴을 상태에 반영(결정 #26/#28). 주사위가 있으면 굴려 변수로 저장. */
function applyTurn(
  input: GameTurnInput,
  t?: (k: string, ...args: any[]) => string
): GameTurnOutcome {
  let state = applyStateDelta(input.state, input.parsed.stateDelta);

  // 선택지를 상태에 영속한다(결정 #28): viewSpec이 순수 함수가 되어 새로고침·
  // 동기화 후에도 마지막 선택지가 복원된다.
  state = { ...state, choices: input.parsed.choices };

  // 주사위 요청이 있으면 시드 RNG로 굴려 lastRoll에 기록(재현 가능, #4).
  if (input.parsed.rolls.length > 0) {
    const first = input.parsed.rolls[0]!;
    state = { ...state, lastRoll: rollDice(first.notation, input.random) };
  }

  const ended = state["ended"] === true || num(state["hp"], 1) <= 0;
  const outcome: GameTurnOutcome = { state, ended };
  if (ended) {
    outcome.endingLabel = num(state["hp"], 1) <= 0 ? (t ? t("game.endingDeath", "사망") : "사망") : (t ? t("game.endingEnd", "엔딩") : "엔딩");
  }
  return outcome;
}

/** 현재 상태에서 렌더할 위젯 선언(결정 #27). */
function viewSpec(
  state: Record<string, unknown>,
  t?: (k: string, ...args: any[]) => string
): ViewSpec {
  const hp = num(state["hp"], 100);
  const maxHp = num(state["maxHp"], 100);
  const inventory = Array.isArray(state["inventory"])
    ? (state["inventory"] as unknown[]).map((x) => String(x))
    : [];
  const choices = Array.isArray(state["choices"])
    ? (state["choices"] as unknown[]).map((x) => String(x))
    : [];
  const ended = state["ended"] === true || hp <= 0;

  const hud: ViewWidget[] = [
    { kind: "stat-bar", label: "HP", value: Math.max(0, hp), max: maxHp, tone: "hp" },
    { kind: "stat-panel", stats: [{ label: t ? t("game.labelLocation", "위치") : "위치", value: String(state["location"] ?? "-") }] },
  ];

  const widgets: ViewWidget[] = [];
  if (inventory.length > 0) {
    widgets.push({ kind: "inventory", title: t ? t("game.labelInventory", "소지품") : "소지품", items: inventory.map((name) => ({ name })) });
  }
  if (ended) {
    widgets.push({
      kind: "notice",
      tone: hp <= 0 ? "danger" : "success",
      text: hp <= 0 ? (t ? t("game.noticeDeath", "당신은 쓰러졌습니다. 게임 종료.") : "당신은 쓰러졌습니다. 게임 종료.") : (t ? t("game.noticeEnd", "모험이 끝났습니다.") : "모험이 끝났습니다."),
    });
  } else if (choices.length > 0) {
    widgets.push({ kind: "choices", choices });
  }

  return { widgets, hud };
}

export const textAdventureModule: GameModule = {
  manifest: {
    id: "text-adventure",
    title: "텍스트 어드벤처",
    titleKey: "game.mod.textAdventure.title",
    description: "AI 게임마스터가 이끄는 선택지 기반 모험. HP·인벤토리·엔딩.",
    descriptionKey: "game.mod.textAdventure.desc",
    kind: "native",
    icon: "compass",
  },
  initialState: (setup, t) => DEFAULT_STATE(t),
  protocolInstructions,
  applyTurn,
  viewSpec,
};

/** 새 텍스트 어드벤처 세션의 초기 모듈 상태. */
export function newTextAdventureState(t?: (k: string, ...args: any[]) => string): Record<string, unknown> {
  return DEFAULT_STATE(t);
}

function num(v: unknown, fallback: number): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}
